import loadable from '@loadable/component';

export type TRouteSsrMode = 'csr' | 'ssr-shell' | 'ssr-content' | 'prerender';

type TRouteComponentWithSSRMeta = {
  ssr?: boolean;
  ssrMode?: TRouteSsrMode;
  __rioRouteLoadableChunkName?: string;
};

export function withRouteSsrMode<T extends TRouteComponentWithSSRMeta>(component: T, ssrMode: TRouteSsrMode): T {
  component.ssrMode = ssrMode;
  component.ssr = ssrMode !== 'csr' && ssrMode !== 'prerender';
  return component;
}

export function withRouteSSRMeta<T extends TRouteComponentWithSSRMeta>(component: T, ssr: boolean): T {
  return withRouteSsrMode(component, ssr ? 'ssr-content' : 'csr');
}

type TLoadableFactory = (loader: any, options?: any) => any;

function getLoaderChunkName(loader: any): string | undefined {
  if (typeof loader?.chunkName !== 'function') {
    return undefined;
  }

  const chunkName = loader.chunkName();
  return typeof chunkName === 'string' && chunkName.length > 0 ? chunkName : undefined;
}

function createRouteLoadableComponent<TLoadable extends TLoadableFactory>(
  loadableFactory: TLoadable,
  loader: Parameters<TLoadable>[0],
  ssrMode: TRouteSsrMode,
  options?: Parameters<TLoadable>[1]
) {
  const component = loadableFactory(loader, options);
  component.__rioRouteLoadableChunkName = getLoaderChunkName(loader);
  return withRouteSsrMode(component, ssrMode);
}

export function createRouteLoadable<TLoadable extends TLoadableFactory>(loadableFactory: TLoadable, ssr: boolean) {
  return (loader: Parameters<TLoadable>[0], options?: Parameters<TLoadable>[1]) =>
    createRouteLoadableComponent(loadableFactory, loader, ssr ? 'ssr-content' : 'csr', options);
}

export function loadableSSR(loader: any, options?: any) {
  return createRouteLoadableComponent(loadable as any, loader, 'ssr-content', options);
}

export function loadableNoSSR(loader: any, options?: any) {
  return createRouteLoadableComponent(loadable as any, loader, 'csr', {
    ...(options || {}),
    ssr: false
  });
}

export function getRouteLoadableChunkName(component: any): string | undefined {
  return component?.__rioRouteLoadableChunkName;
}
